import { useMemo, useRef, useState } from 'react';
import { useToast } from '../../components/ui';
import { sendOtp, verifyOtp, GetGuestInvitesByMobile, SubmitGuestRsvp } from '../../api/api';

const ten = (v) => v.replace(/\D/g, '').slice(0, 10);

// Guest invitation flow (no app login needed):
//   1. 'mobile'  — guest enters their mobile, we WhatsApp an OTP
//   2. 'otp'     — guest enters the 4-digit OTP
//   3. 'invites' — we load every invitation for that mobile, grouped by event →
//                  function, and let them RSVP (Yes / No / Maybe) per function.
// Shared by the desktop and mobile invite UIs; only presentation differs.
export function useInvite() {
  const toast = useToast();
  const [step, setStep] = useState('mobile');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);
  const [reused, setReused] = useState(null); // Date_Time of an earlier, still-valid OTP
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const otpRequested = useRef(false); // guard against a double send

  const sendCode = async () => {
    setErr('');
    if (!/^\d{10}$/.test(mobile)) { setErr('Enter a valid 10-digit mobile number'); return; }
    setBusy(true);
    try {
      const r = await sendOtp({ MobileNumber: mobile, ServiceBy: 'Mehfil by Smartify' });
      setReused(r?.reused ? r.Date_Time : null);
      otpRequested.current = true;
      setSent(true); setStep('otp');
    } catch {
      setErr('Could not send OTP. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setOtp(''); setErr('');
    try {
      const r = await sendOtp({ MobileNumber: mobile, ServiceBy: 'Mehfil by Smartify' });
      setReused(r?.reused ? r.Date_Time : null);
      toast(r?.reused ? 'Your earlier OTP is still valid (1 hour) — use that code' : 'OTP sent again');
    }
    catch { setErr('Could not resend OTP'); }
  };

  const loadInvites = async () => {
    setLoading(true);
    try {
      const rows = await GetGuestInvitesByMobile({ MobileNumber: mobile });
      setInvites(Array.isArray(rows) ? rows : []);
    } catch {
      toast('Could not load your invitations');
      setInvites([]);
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setErr('');
    if (!/^\d{4}$/.test(otp)) { setErr('Enter the 4-digit OTP'); return; }
    setBusy(true);
    try {
      const res = await verifyOtp({ MobileNumber: mobile, OTP: otp });
      if (!res?.verified) { setErr(res?.reason || 'Invalid OTP'); return; }
      setStep('invites');
      await loadInvites();
    } catch {
      setErr('Verification failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const rsvp = async (row, answer) => {
    setSavingId(row.GuestId);
    try {
      // For "No" the head-count is 0; otherwise keep their expected count.
      const ConfirmedCount = answer === 'No' ? 0 : (row.ConfirmedCount || row.ExpectedCount || 1);
      await SubmitGuestRsvp({ GuestId: row.GuestId, MobileNumber: mobile, RSVPStatus: answer, ConfirmedCount });
      setInvites(list => list.map(r =>
        r.GuestId === row.GuestId ? { ...r, RSVPStatus: answer, ConfirmedCount } : r));
      toast(answer === 'Yes' ? 'See you there!' : answer === 'No' ? 'Response saved' : 'Marked as Maybe');
    } catch {
      toast('Could not save your response');
    } finally {
      setSavingId(null);
    }
  };

  const setConfirmedCount = (row, n) => {
    const ConfirmedCount = Math.max(0, Number(n) || 0);
    setInvites(list => list.map(r => r.GuestId === row.GuestId ? { ...r, ConfirmedCount } : r));
  };

  // Reset everything (e.g. "use a different number").
  const restart = () => {
    setStep('mobile'); setOtp(''); setErr(''); setSent(false); setReused(null);
    setInvites([]); otpRequested.current = false;
  };

  // Group invitations by event, each with its function rows.
  const groups = useMemo(() => {
    const m = new Map();
    invites.forEach(r => {
      const key = r.EventId ?? `e:${r.EventName || ''}`;
      if (!m.has(key)) m.set(key, { EventId: r.EventId, EventName: r.EventName || 'Event', EventType: r.EventType, rows: [] });
      m.get(key).rows.push(r);
    });
    return Array.from(m.values());
  }, [invites]);

  // The guest's display name (from the first invite row).
  const guestName = invites[0]
    ? ([invites[0].Surname, invites[0].GuestName].filter(Boolean).join(' ').trim() || invites[0].GuestName)
    : '';

  return {
    step, mobile, setMobile: (v) => setMobile(ten(v)), otp, setOtp: (v) => setOtp(v.replace(/\D/g, '').slice(0, 4)),
    busy, err, sent, loading, savingId, invites, groups, guestName,
    sendCode, verify, resend, rsvp, setConfirmedCount, restart, reused,
  };
}
